import { Component, effect, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DeviceFilterType } from '../../services/types';
import { DashboardService } from '../../services/dashboard.service';
import { BrandsSelectionPanelComponent } from '../brands-selection-panel/brands-selection-panel.component';
import { A11yModule } from "@angular/cdk/a11y";
import { CommonModule } from '@angular/common';
import { DatePickerComponent, FixedDateRanges } from '../../shared/components/date-picker/date-picker.component';
import { DateRangeValue } from '../../utils/time';
import { SelectItem, SelectMenuComponent, SelectMode } from '../../shared/components/select-menu/select-menu.component';
import { UtilService } from '../../services/util.service';
import { AssetVersionPipe } from '../../pipes/asset-version.pipe';
import { StateService } from '../../services/state.service';
import { DataTestIds } from '../../../../e2e/types';
import { AuthorizationService } from '../../services/authorization.service';

export enum DashboardType {
  MY_BRAND = 'myBrand',
  MY_COMPETITION = 'myCompetition',
  MY_CAMPAIGNS = 'myCampaigns',
  AI_PRESENCE = 'aiPresence'
}

@Component({
  selector: 'app-top-bar-wrapper',
  imports: [
    SelectMenuComponent,
    BrandsSelectionPanelComponent,
    A11yModule,
    CommonModule,
    DatePickerComponent,
    AssetVersionPipe
  ],
  templateUrl: './top-bar-wrapper.component.html',
  styleUrl: './top-bar-wrapper.component.scss'
})
export class TopBarWrapperComponent {
  @Input() type?: DashboardType;

  protected authorizationService = inject(AuthorizationService);
  protected utilService = inject(UtilService);
  protected dashboardService = inject(DashboardService);
  protected stateService = inject(StateService);
  protected readonly SelectMode = SelectMode;
  protected readonly DashboardType = DashboardType;
  protected readonly DeviceFilterType = DeviceFilterType;
  protected displaySecondBar = false;

  protected readonly deviceOptions: SelectItem<DeviceFilterType>[] = [
    { key: DeviceFilterType.ALL_DEVICES, label: DeviceFilterType.ALL_DEVICES, icon: 'svg/all-devices.svg' },
    { key: DeviceFilterType.DESKTOP, label: DeviceFilterType.DESKTOP, icon: 'svg/device-desktop.svg' },
    { key: DeviceFilterType.MOBILE, label: DeviceFilterType.MOBILE, icon: 'svg/device-mobile.svg' }
  ];
  protected readonly channelsOptions: SelectItem<string>[] = [
    { key: 'openWeb', label: 'Open Web', disabled: false },
    { key: 'social', label: 'Social - Coming soon', disabled: true },
    { key: 'paid', label: 'Paid Search - Coming soon', disabled: true },
    { key: 'ctv', label: 'CTV - Coming soon', disabled: true },
  ];
  protected readonly mediaTypesOptions: SelectItem<string>[] = [
    { key: 'display', label: 'Display', disabled: false },
    { key: 'video', label: 'Video - Coming soon', disabled: true },
    { key: 'native', label: 'Native Ads - Coming soon', disabled: true },
  ];

  protected dateRangeControl: FormControl<number | null> = new FormControl();
  protected countriesControl: FormControl<string[] | null> = new FormControl([]);
  protected deviceControl: FormControl<string | null> = new FormControl();

  protected myBrand = this.dashboardService.myBrand;
  protected myCompetitors = this.dashboardService.myCompetitors;
  protected myBrandOptions = this.dashboardService.myBrandOptions;
  protected myCompetitorsOptions = this.dashboardService.myCompetitorsOptions;
  protected dateRange = this.dashboardService.dateRange;

  protected userRelatedBrand = this.dashboardService.userRelatedBrand;
  protected userRelatedBrandOptions = this.dashboardService.userRelatedBrandOptions;

  protected channelsControl: FormControl<string[]> = new FormControl();
  protected mediaTypesControl: FormControl<string[]> = new FormControl();
  protected dateRangeGroup: FormGroup<{
    startDate: FormControl<Date | null>;
    endDate: FormControl<Date | null>;
    relativeDate: FormControl<FixedDateRanges | undefined>;
  }> = new FormGroup({
    startDate: new FormControl<Date | null>(null),
    endDate: new FormControl<Date | null>(null),
    relativeDate: new FormControl<FixedDateRanges | undefined>(undefined) as FormControl<FixedDateRanges | undefined>
  });
  protected countriesOptions = this.dashboardService.countriesOptions;
  protected isTrialUser: boolean = false;
  protected displayUserRelatedBrand: boolean = false;
  protected displaySelectionPanel: boolean = false;
  protected myCompetitorsDisabled: boolean = true;
  protected readonly DataTestIds = DataTestIds;

  constructor() {
    effect(() => {
      const dateRangeValue = this.dashboardService.dateRange();
      this.dateRangeGroup.patchValue({
        startDate: dateRangeValue.startDate ? (dateRangeValue.startDate instanceof Date ? dateRangeValue.startDate : new Date(dateRangeValue.startDate)) : null,
        endDate: dateRangeValue.endDate ? (dateRangeValue.endDate instanceof Date ? dateRangeValue.endDate : new Date(dateRangeValue.endDate)) : null,
        relativeDate: dateRangeValue.relativeDate
      }, { emitEvent: false });
    });

    effect(() => {
      const countries = this.dashboardService.countries();
      this.countriesControl.setValue(countries, { emitEvent: false });
    });

    effect(() => {
      const device = this.dashboardService.device();
      this.deviceControl.setValue(device, { emitEvent: false });
    });
  }

  ngOnInit() {
    this.displayUserRelatedBrand = this.type === DashboardType.AI_PRESENCE;
    this.displaySelectionPanel = this.shouldDisplaySelectionPanel();
    this.myCompetitorsDisabled = this.isMyCompetitorsDisabled();
    this.isTrialUser = this.authorizationService.isTrialUser;
  }

  protected toggleDisplaySecondBar(): void {
    this.displaySecondBar = !this.displaySecondBar;
  }

  protected onDateRangeChange(dates: DateRangeValue): void {
    this.dashboardService.updateDateRange(dates);
  }

  protected onCountryChange(): void {
    this.dashboardService.updateCountries(this.countriesControl.value as string[]);
  }

  protected onDeviceChange(): void {
    this.dashboardService.updateDevice(this.deviceControl.value as DeviceFilterType);
  }

  protected onMyBrandChanged(brandKey: string): void {
    this.dashboardService.updateMyBrand(brandKey);
  }

  protected onCompetitorChanged({ competitorKey, previousCompetitorKey }: { competitorKey: string, previousCompetitorKey: string }): void {
    this.dashboardService.updateCompetitor(competitorKey, previousCompetitorKey);
  }

  protected onCompetitorRemoved(competitorKey: string): void {
    this.dashboardService.removeCompetitor(competitorKey);
  }

  protected onCompetitorAdded(competitorKey: string): void {
    this.dashboardService.addCompetitor(competitorKey);
  }

  protected onUserRelatedBrandChanged(brandKey: string): void {
    this.dashboardService.updateUserRelatedBrand(brandKey);
  }

  private shouldDisplaySelectionPanel(): boolean {
    return [
      DashboardType.MY_COMPETITION,
      DashboardType.MY_BRAND,
      DashboardType.AI_PRESENCE
    ].includes(this.type!);
  }

  protected shouldDisplayAddButton(): boolean {
    const competitors = this.myCompetitors();
    return (this.type === DashboardType.MY_COMPETITION)
      && !!competitors?.length && competitors?.length < 5;
  }

  protected shouldDisplayRemoveButton(): boolean {
    const competitors = this.myCompetitors();
    return (this.type === DashboardType.MY_COMPETITION)
      && !!competitors?.length
      && competitors?.length > 1;
  }

  private isMyCompetitorsDisabled(): boolean {
    return this.type === DashboardType.MY_BRAND;
  }
}
